import empty from "is-empty";
import moment from "moment";

import { Confirmations } from "./types";
import * as Validators from "src/utils/validators";
import { ConfirmationSelectors } from "./selectors";
import { SpinnerActions } from "src/redux/Spinner/actions";
import fetchWrapper from "src/utils/fetchWrapper";
import { COLLAT_TYPES } from "src/redux/Tx/constants";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { setDateFormat } from "src/utils";
import { CONFIRM_EDIT_MODE } from "./constants";
import { ConfirmActions } from "../Confirm/actions";
import { SpinnerTypes } from "src/redux/Spinner/types";
import { FileManagerActions } from "../FileManager/actions";
import { MdActions } from "src/redux/MarketData/actions";
import { FileManagerSelectors } from "../FileManager/selectors";
import { ModalActions } from "src/redux/Modal/actions";
import { SettingsSelectors } from "src/redux/Settings/selectors";

const buildRequest = (rowData, canEditAll = false) => {
    if (canEditAll) {
        let rateBp = Math.round(
            Validators.parseFloatToSizeLimit(rowData.rate) * 100
        );

        let result = {
            id: rowData.confirmation_id,
            quantity: Validators.parseIntToSizeLimit(rowData.quantity),
            fee: { rateBp },
            rateRebate: { rateBp },
            valueDate: setDateFormat(rowData.value_date, "-"),
            callable: rowData.callable,
            dividend: Validators.parseFloatToSizeLimit(rowData.dividend),
            maturityDate:
                setDateFormat(rowData.maturity_date, "-") || "9999-12-31",
            objective: rowData.objective,
            collateralType: {
                type: COLLAT_TYPES[rowData.collateral_type],
                ccy: rowData.collateral_currency
                    ? [rowData.collateral_currency]
                    : null,
            },
        };

        return result;
    }

    return {
        id: rowData.confirmation_id,
        objective: rowData.objective,
    };
};

const buildExtConfirmation = (row) => {
    let newRow = {
        quantity: row.Quantity,
        security: row.marketData.data,
        valueDate: setDateFormat(row.ValueDate, "-"),
        maturityDate: setDateFormat(row.Maturity, "-") || "9999-12-31",
        confirmedTime: moment(row.ConfirmedTime)
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss"),
        dividend: row.Dividend,
        callable: row.Callable,
        intermediary: row.Intermediary,
        txType: row.TXType,
        myFundName: row.MyFundName,
        cptyFundName: row.CptyFundName,
        myFundCode: row.MyFundCode,
        cptyFundCode: row.CptyFundCode,
        myAccountName: row.MyAccountName,
        cptyAccountName: row.CptyAccountName,
        myAccountCode: row.MyAccountCode,
        cptyAccountCode: row.CptyAccountCode,
        mySlbNumber: row.MySlbNo,
        cptySlbNumber: row.CptySlbNo,
        myCustodyAssetType: row.MyCstdAssetType,
        cptyCustodyAssetType: row.CptyCstdAssetType,
        purpose: row.Purpose,
    };

    if (row.CollateralType) {
        newRow.collateralType = {};
        newRow.collateralType.type = COLLAT_TYPES[row.CollateralType];
    }
    if (row.CollateralCCY) {
        newRow.collateralType = newRow.collateralType || {};
        newRow.collateralType.ccy = [row.CollateralCCY];
    }

    if (row.FR === "R") {
        newRow.rateRebate = { rateBp: Math.round(row.Rate * 100) };
    } else {
        newRow.fee = { rateBp: Math.round(row.Rate * 100) };
    }

    return newRow;
};

const cancelConfirmations = (dispatch, state, comment) => {
    dispatch(
        SpinnerActions.Open({ spinnerType: SpinnerTypes.CANCEL_CONFIRMATION })
    );
    const txIds = ConfirmationSelectors.GetSelectedRows(state).map(
        (r) => r.confirmation_id
    );
    let request = {
        comment,
        txIds,
    };
    return dispatch(
        fetchWrapper(AuthSelectors.BuildLenderUrl(state), {
            method: "DELETE",
            body: JSON.stringify(request),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        })
    )
        .then((result) => {
            // update table with new status / comment
            const newUpdates = { ids: txIds, status: "CANCELED" };
            if (!empty(comment)) newUpdates["comment"] = comment;
            dispatch({
                type: Confirmations.SET_NEW_UPDATES,
                newUpdates,
            });
            dispatch(
                SpinnerActions.Close({
                    spinnerType: SpinnerTypes.CANCEL_CONFIRMATION,
                })
            );
        })
        .catch((err) => {
            dispatch(
                SpinnerActions.Close({
                    spinnerType: SpinnerTypes.CANCEL_CONFIRMATION,
                })
            );
        });
};
const updateConfirmations = (dispatch, state, comment) => {
    dispatch(
        SpinnerActions.Open({ spinnerType: SpinnerTypes.MODIFY_CONFIRMATION })
    );
    const txSet = [];
    const changes = ConfirmationSelectors.GetTableChanges(state);
    const {
        "enable-ui-lender-edit-confirmation-all-fields":
            enabledLenderEditAllFields,
    } = SettingsSelectors.FeatureFlags(state);
    Object.keys(changes).forEach((key) => {
        const data = buildRequest(
            changes[key].rowData,
            enabledLenderEditAllFields
        );
        txSet.push(data);
    });
    let request = { txSet };
    if (!empty(comment)) request.comment = comment.trim();

    return dispatch(
        fetchWrapper(AuthSelectors.BuildLenderUrl(state), {
            method: "PUT",
            body: JSON.stringify(request),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        })
    )
        .then((result) => {
            // update table with new comment
            if (!empty(comment)) {
                dispatch({
                    type: Confirmations.SET_NEW_UPDATES,
                    newUpdates: { ids: txSet.map((t) => t.id), comment },
                });
            }
            dispatch({
                // turn off edit mode
                type: Confirmations.SET_EDIT_MODE,
                value: CONFIRM_EDIT_MODE.OFF_WITH_SAVE,
            });
        })
        .catch((err) => {
            dispatch(
                SpinnerActions.Close({
                    spinnerType: SpinnerTypes.MODIFY_CONFIRMATION,
                })
            );
            dispatch(
                ConfirmActions.Open({
                    message:
                        "There was an error performing this edit. Please contact TruWeb Support",
                    title: "Error",
                    testId: "confirm-update-error",
                    warning: true,
                })
            );
        });
};

export const ConfirmationActions = {
    SetReadyToRefresh: (value) => {
        return (dispatch, getState) => {
            // request to server for data
            return dispatch({
                type: Confirmations.SET_READY_TO_REFRESH,
                value,
            });
        };
    },
    SetTableStatus: (value) => {
        return (dispatch, getState) => {
            return dispatch({
                type: Confirmations.SET_TABLE_STATUS,
                ...value,
            });
        };
    },
    SetEditMode: (value) => {
        return (dispatch, getState) => {
            // request to server for data
            return dispatch({
                type: Confirmations.SET_EDIT_MODE,
                value,
            });
        };
    },
    ResetTableChanges: () => {
        return (dispatch, getState) => {
            dispatch({
                type: Confirmations.RESET_TABLE_CHANGES,
            });
            return dispatch({
                type: Confirmations.RESET_TABLE_ERRORS,
            });
        };
    },
    SetTableChanges: (value) => {
        return (dispatch, getState) => {
            dispatch({
                type: Confirmations.SET_TABLE_CHANGES,
                value,
            });

            // check error after this field is updated.
            const errorType = Validators.getConfirmFieldErrorType(
                value,
                ConfirmationSelectors.GetTableChanges(getState())
            );
            return dispatch({
                type: Confirmations.SET_TABLE_ERRORS,
                value: { id: value.id, fieldName: value.fieldName, errorType },
            });
        };
    },
    SetSelectedRows: (value) => {
        return (dispatch, getState) => {
            return dispatch({
                type: Confirmations.SET_SELECTED_ROWS,
                selectedRows: value,
            });
        };
    },
    ResetCancelErrors: () => {
        return (dispatch, getState) => {
            return dispatch({
                type: Confirmations.SET_CANCEL_ERRORS,
                cancelErrors: null,
            });
        };
    },
    SetNewUpdates: (newUpdates) => {
        return (dispatch, getState) => {
            return dispatch({
                type: Confirmations.SET_NEW_UPDATES,
                newUpdates,
            });
        };
    },
    EditModeCancel: () => {
        return (dispatch, getState) => {
            const state = getState();
            const tableChanges = ConfirmationSelectors.GetTableChanges(state);
            const offWithoutSave = () =>
                dispatch({
                    type: Confirmations.SET_EDIT_MODE,
                    value: CONFIRM_EDIT_MODE.OFF_WITHOUT_SAVE,
                });

            if (!empty(tableChanges)) {
                dispatch(
                    ConfirmActions.Open({
                        message:
                            "Do you really want to exit without making the change?",
                        cancelTitle: "No",
                        okTitle: "Yes",
                        title: "Notice",
                        testId: "confirm-confirmation-exit",
                        backdrop: true,
                        onOk: () => offWithoutSave(),
                    })
                );
            } else {
                offWithoutSave();
            }
        };
    },
    EditModeSave: () => {
        return (dispatch, getState) => {
            const state = getState();
            const tableChanges = ConfirmationSelectors.GetTableChanges(state);
            const keys = Object.keys(tableChanges);
            const counterparties = [];
            keys.forEach((key) => {
                const { borrower_name } = tableChanges[key].rowData;
                if (!counterparties.includes(borrower_name)) {
                    counterparties.push(borrower_name);
                }
            });
            const message =
                `You are about to edit ${keys.length} \nconfirmations for ` +
                `${counterparties.length} counterparties. \nAre you sure?`;
            dispatch(
                ConfirmActions.Open({
                    message,
                    title: "Submit",
                    cancelTitle: "No",
                    okTitle: "Yes",
                    getDescription: true,
                    descriptionPlaceholder:
                        "Optional. Tell your CPTY the reason for the change.",
                    testId: "confirm-confirmation-submit",
                    backdrop: true,
                    onOk: (comment) => {
                        const tableErrors =
                            ConfirmationSelectors.GetTableErrors(state);
                        if (empty(tableErrors)) {
                            updateConfirmations(dispatch, state, comment);
                        }
                    },
                })
            );
        };
    },
    CancelConfirmations: () => {
        return (dispatch, getState) => {
            const state = getState();
            const selectedRows = ConfirmationSelectors.GetSelectedRows(state);
            const errors = [];
            const counterparties = [];
            selectedRows.forEach((row) => {
                const { borrower_name } = row;
                if (
                    row.status === "CANCELED" ||
                    row.status === "EXPIRED" ||
                    !Validators.isCancellable(
                        row.value_date,
                        row.security_country
                    ) ||
                    row.IsPartnerTrade ||
                    row.borrower.startsWith("EXT-")
                ) {
                    errors.push(row.confirmation_id);
                }
                if (!counterparties.includes(borrower_name)) {
                    counterparties.push(borrower_name);
                }
            });
            dispatch({
                type: Confirmations.SET_CANCEL_ERRORS,
                cancelErrors: errors,
            });
            if (errors.length > 0) {
                dispatch(
                    ConfirmActions.Open({
                        message:
                            "The items you have selected cannot be canceled.",
                        title: "Error",
                        testId: "confirm-confirmation-cancel-error",
                        warning: true,
                    })
                );
            } else {
                const message =
                    `You are about to cancel ${selectedRows.length} \nconfirmations for ` +
                    `${counterparties.length} counterparties. \nAre you sure?`;
                dispatch(
                    ConfirmActions.Open({
                        message,
                        title: "Cancel",
                        cancelTitle: "No",
                        okTitle: "Yes",
                        getDescription: true,
                        testId: "confirm-confirmation-cancel",
                        descriptionPlaceholder:
                            "Optional. Tell your CPTY the reason for the change. 100 characters at max",
                        backdrop: true,
                        onOk: (comment) =>
                            cancelConfirmations(dispatch, state, comment),
                    })
                );
            }
        };
    },

    SetDetail: (confirmation) => {
        return (dispatch) => {
            if (confirmation) {
                dispatch(
                    FileManagerActions.BuildConfirmationAttachmentsPath(
                        confirmation
                    )
                );
            } else {
                dispatch({
                    type: Confirmations.SELECT_CONFIRMATION,
                    confirmation,
                });
            }
        };
    },

    OpenDetail: () => {
        return (dispatch, getState) => {
            const state = getState();
            const selectedConfirmations =
                ConfirmationSelectors.GetSelectedRows(state);
            if (selectedConfirmations.length === 1) {
                dispatch(
                    FileManagerActions.BuildConfirmationAttachmentsPath(
                        selectedConfirmations[0]
                    )
                );
            } else {
                dispatch({
                    type: Confirmations.SELECT_CONFIRMATION,
                    confirmation: null,
                });
            }
        };
    },

    PrepareStore: (rowData, enable90DayMaxMaturity) => {
        return (dispatch) => {
            dispatch({
                type: Confirmations.PREPARE_STORE,
                rowData,
            });

            const listTicker = [
                ...new Set(rowData.map((row) => row.SecurityId)),
            ];
            dispatch(MdActions.GetMarketDataBatch(listTicker)).then((result) =>
                dispatch({
                    type: Confirmations.ATTACH_MARKET_DATA,
                    marketData: result,
                    listTicker: listTicker,
                    enable90DayMaxMaturity,
                })
            );
        };
    },

    ResetStore: () => {
        return (dispatch) => {
            dispatch({ type: Confirmations.RESET_STORE });
        };
    },

    UpdateStoreRow: (rowData, colId, featureFlags) => ({
        type: Confirmations.UPDATE_STORE_ROW,
        rowData,
        colId,
        featureFlags,
    }),

    DeleteStoreRow: (rowId) => ({
        type: Confirmations.DELETE_STORE_ROW,
        rowId,
    }),

    ApplyMaturity: (maturity, enable90DayMaxMaturity) => ({
        type: Confirmations.APPLY_MATURITY,
        maturity,
        enable90DayMaxMaturity,
    }),

    SetStoreSettings: (settings) => ({
        type: Confirmations.SET_STORE_SETTINGS,
        settings,
    }),

    SetStoreErrors: (errors) => ({
        type: Confirmations.SET_STORE_ERRORS,
        errors,
    }),

    SendStore: () => {
        return (dispatch, getState) => {
            const state = getState();

            dispatch(SpinnerActions.Open());

            // TODO: sanity check before sending request
            const enablePurpose =
                state.Settings?.featureFlags["enable-purpose"];
            const { cptyName, comment } =
                ConfirmationSelectors.GetStoreSettings(state);
            const confirmations = ConfirmationSelectors.GetStoreRows(state)
                .map(buildExtConfirmation)
                .map((row) => {
                    if (!enablePurpose) {
                        delete row.purpose;
                    }
                    return row;
                });
            const { files, uuid } = FileManagerSelectors.GetUploadedFils(state);
            const attachments = files.map((fileName) => `${uuid}/${fileName}`);

            const url = AuthSelectors.BuildStoreExtConfirmationsUrl(state);

            return dispatch(
                fetchWrapper(url, {
                    method: "POST",
                    body: JSON.stringify({
                        confirmations,
                        comment,
                        cptyName,
                        attachments,
                    }),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then((result) => {
                    dispatch(ConfirmationActions.ResetStore());
                    dispatch(FileManagerActions.ResetFiles(false));
                    dispatch(SpinnerActions.Close());
                    dispatch(ModalActions.CloseAll());
                })
                .catch((error) => {
                    dispatch(SpinnerActions.Close());
                });
        };
    },
    SetConfirmations: (responseData) => ({
        type: Confirmations.SET_CONFIRMATIONS,
        responseData,
    }),
    fetchFilterOptions: (field) => {
        return (dispatch, getState) => {
            const state = getState();
            const url =
                AuthSelectors.BuildStoreConfirmationsFilterValueUrl(state);
            const fields = [field];
            return dispatch(
                fetchWrapper(url, {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ fields }),
                })
            )
                .then((result) => {
                    dispatch({
                        type: Confirmations.SET_CONFIRMATIONS_FILTER_OPTIONS,
                        payload: result?.data,
                    });
                })
                .catch((error) => {
                    console.log("ERROR", error);
                });
        };
    },
};
